/**
 * AuthContext — single source of truth for the authenticated identity.
 *
 * Flow:
 *  1. Firebase onAuthStateChanged fires when a user signs in/out.
 *  2. If signed in, we call GET /v1/auth/me to obtain the backend AuthContext
 *     (uid, email, tenant_id, role, tenant_name, timezone, currency).
 *  3. If /v1/auth/me returns 401 (claims not set, token stale) we sign out
 *     cleanly so the user ends up at /login with a clear error.
 *  4. On sign-out we clear the QueryClient cache and Firebase session.
 *
 * The frontend NEVER reads Firestore directly — all identity info comes
 * from the backend via fetchMe().
 */

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { auth } from '@core/infrastructure/firebase';
import { queryClient } from '@core/api/queryClient';
import { fetchMe, AUTH_ME_KEY } from '@core/auth/api';
import type { AuthUser, Role } from '@/types';

// -------------------------------------------------------------------------
// Context shape
// -------------------------------------------------------------------------

interface AuthContextType {
  /** Resolved backend identity — null while loading or signed out. */
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  /** Sign in with email + password via Firebase Auth. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign out: clears Firebase session + query cache. */
  signOut: () => Promise<void>;
  /** True if the user holds at least one of the given roles. */
  hasRole: (...roles: Role[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// -------------------------------------------------------------------------
// Provider
// -------------------------------------------------------------------------

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch backend identity after Firebase Auth confirms a session.
  const resolveUser = useCallback(async (_firebaseUser: FirebaseUser): Promise<void> => {
    try {
      // fetchMe injects the Firebase ID token automatically via apiCall.
      const authUser = await queryClient.fetchQuery({
        queryKey: AUTH_ME_KEY,
        queryFn: fetchMe,
        staleTime: 5 * 60_000,
      });
      setUser(authUser);
      setError(null);
    } catch (err: unknown) {
      // 401 = claims not set or token rejected by backend → sign out cleanly.
      const status = (err as { status?: number })?.status;
      if (status === 401) {
        setError('Your account is not yet configured. Please contact your administrator.');
        await firebaseSignOut(auth);
        queryClient.clear();
        setUser(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load user profile');
        setUser(null);
      }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true);
      if (firebaseUser) {
        await resolveUser(firebaseUser);
      } else {
        setUser(null);
        queryClient.removeQueries({ queryKey: AUTH_ME_KEY });
      }
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, [resolveUser]);

  const signIn = async (email: string, password: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // resolveUser is called by the onAuthStateChanged listener above.
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
      queryClient.clear();
      setUser(null);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign out';
      setError(message);
      throw err;
    }
  };

  const hasRole = (...roles: Role[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role as Role);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        isLoading,
        error,
        signIn,
        signOut,
        hasRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// -------------------------------------------------------------------------
// Hook
// -------------------------------------------------------------------------

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

